import {
  animate,
  sequence,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  viewChild,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { type ThemePalette } from '@angular/material/core';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
  selector: 'pt-loader-button',
  templateUrl: './loader-button.component.html',
  styleUrls: ['./loader-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('loaderAnimation', [
      state(
        'hide',
        style({
          width: '0px',
          opacity: '0',
          'margin-right': '0px',
        })
      ),
      state(
        'show',
        style({
          width: '20px',
          opacity: '1',
          'margin-right': '8px',
        })
      ),
      transition(
        'hide => show',
        sequence([
          animate(
            '200ms',
            style({
              width: '20px',
              'margin-right': '8px',
            })
          ),
          animate(
            '200ms',
            style({
              opacity: '1',
            })
          ),
        ])
      ),
      transition(
        'show => hide',
        sequence([
          animate(
            '200ms',
            style({
              opacity: '0',
            })
          ),
          animate(
            '200ms',
            style({
              width: '0px',
              'margin-right': '0px',
            })
          ),
        ])
      ),
    ]),
  ],
  host: {
    class: 'flex items-stretch',
    '[class.cta]': 'cta()',
    '(click)': 'onClick($event)',
  },
  imports: [NgClass, NgTemplateOutlet, MatButton, MatIcon, MatProgressSpinner],
})
export class LoaderButtonComponent {
  readonly type = input<'stroked' | 'flat' | 'raised'>();
  readonly cta = input(false, { transform: booleanAttribute });
  readonly loading = input(false, { transform: booleanAttribute });
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly color = input<ThemePalette>();
  readonly classes = input<string>();

  readonly clicked = output<void>();

  readonly button = viewChild(MatButton);

  readonly isDisabled = computed(() => this.disabled() || this.loading());
  readonly classString = computed(() => {
    const classes = this.classes();
    return classes ? `flex-1 ${classes}` : 'flex-1';
  });

  onClick(event: MouseEvent): void {
    const button = this.button();
    if (button?.disabled) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    this.clicked.emit();
  }
}
